// Powers the county combobox: clicking/focusing the field (or the chevron
// button) opens the full, letter-grouped list to browse like a native
// <select>, and typing narrows it live. Picking a county (click, or Enter on
// the highlighted option) toggles `.hidden` on rep cards that don't carry
// that county slug as a class (see sales-rep-card.twig). This replaced a
// plain <select> because a flat 70+ option native dropdown was too long to
// scan even grouped by letter, and it isn't built on <input list>/<datalist>
// because iOS Safari doesn't render datalist suggestions at all.
export function initSalesTeamFilter(): void {
  const wrapper = document.querySelector<HTMLElement>(".county-filter");
  const input = document.querySelector<HTMLInputElement>(".select-county");
  const listbox = document.querySelector<HTMLUListElement>(".county-listbox");
  const toggle = document.querySelector<HTMLButtonElement>(".county-toggle");
  if (!wrapper || !input || !listbox) return;

  const options = Array.from(listbox.querySelectorAll<HTMLLIElement>(".county-option"));
  const emptyMessage = listbox.querySelector<HTMLLIElement>(".county-empty");
  const cards = document.querySelectorAll<HTMLElement>(".sales-rep-container");

  let activeIndex = -1;

  const applyFilter = (value: string) => {
    cards.forEach((card) => {
      card.classList.toggle("hidden", !card.classList.contains(value));
    });
  };

  const visibleOptions = () => options.filter((option) => !option.hidden);

  // A group's letter header only makes sense while at least one county
  // under it is still showing, otherwise a filtered search reads as
  // "B / (nothing) / C / Calumet" instead of just "C / Calumet".
  const updateGroupLabels = () => {
    let currentGroup: HTMLLIElement | null = null;
    let groupHasVisible = false;

    Array.from(listbox.children).forEach((child) => {
      if (!(child instanceof HTMLLIElement)) return;

      if (child.classList.contains("county-group-label")) {
        if (currentGroup) currentGroup.hidden = !groupHasVisible;
        currentGroup = child;
        groupHasVisible = false;
      } else if (child.classList.contains("county-option") && !child.hidden) {
        groupHasVisible = true;
      }
    });

    if (currentGroup) (currentGroup as HTMLLIElement).hidden = !groupHasVisible;
  };

  const clearActive = () => {
    options.forEach((option) => option.classList.remove("is-active"));
    activeIndex = -1;
    input.removeAttribute("aria-activedescendant");
  };

  const closeList = () => {
    listbox.hidden = true;
    wrapper.classList.remove("is-open");
    input.setAttribute("aria-expanded", "false");
    clearActive();
  };

  const openList = () => {
    listbox.hidden = false;
    wrapper.classList.add("is-open");
    input.setAttribute("aria-expanded", "true");
  };

  const setActive = (index: number) => {
    const visible = visibleOptions();
    options.forEach((option) => option.classList.remove("is-active"));

    if (index < 0 || index >= visible.length) {
      activeIndex = -1;
      input.removeAttribute("aria-activedescendant");
      return;
    }

    activeIndex = index;
    const option = visible[index];
    option.classList.add("is-active");
    input.setAttribute("aria-activedescendant", option.id);
    option.scrollIntoView({ block: "nearest" });
  };

  const selectOption = (option: HTMLLIElement) => {
    input.value = option.textContent?.trim() ?? "";
    applyFilter(option.dataset.value ?? "");
    closeList();
  };

  // Renders the list for a given query (empty = everything, grouped by
  // letter) and always opens it — this is what powers both the "click to
  // browse" dropdown and "type to search" filtering with the same code path.
  const filterList = (query: string) => {
    let anyVisible = false;

    options.forEach((option) => {
      const matches = query === "" || (option.textContent ?? "").toLowerCase().includes(query);
      option.hidden = !matches;
      if (matches) anyVisible = true;
    });

    updateGroupLabels();
    if (emptyMessage) emptyMessage.hidden = anyVisible;
    clearActive();
    openList();
  };

  const refreshList = () => filterList(input.value.trim().toLowerCase());

  // The field shows the picked county's label until it's interacted with
  // again, at which point it resets to a blank slate (full, unfiltered
  // list) rather than re-filtering by whatever label is still sitting there.
  const resetToBlankSlate = () => {
    input.value = "";
    filterList("");
  };

  input.addEventListener("input", refreshList);
  input.addEventListener("focus", resetToBlankSlate);
  // Picking an option keeps the input focused (see the mousedown handler
  // below), so a later click on it won't re-fire "focus" — reset here too.
  input.addEventListener("click", () => {
    if (listbox.hidden) resetToBlankSlate();
  });

  input.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (listbox.hidden) resetToBlankSlate();
      const visible = visibleOptions();
      if (visible.length === 0) return;
      setActive((activeIndex + 1) % visible.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const visible = visibleOptions();
      if (visible.length === 0) return;
      setActive((activeIndex - 1 + visible.length) % visible.length);
    } else if (event.key === "Enter") {
      const visible = visibleOptions();
      if (activeIndex >= 0 && visible[activeIndex]) {
        event.preventDefault();
        selectOption(visible[activeIndex]);
      }
    } else if (event.key === "Escape") {
      closeList();
    }
  });

  toggle?.addEventListener("mousedown", (event) => event.preventDefault());
  toggle?.addEventListener("click", () => {
    if (listbox.hidden) {
      input.focus();
      resetToBlankSlate();
    } else {
      closeList();
    }
  });

  // mousedown fires before the input's blur, so preventing its default here
  // stops the listbox from closing before the click handler below gets a
  // chance to read which option was clicked.
  options.forEach((option) => {
    option.addEventListener("mousedown", (event) => event.preventDefault());
    option.addEventListener("click", () => selectOption(option));
  });

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Node)) return;
    if (!wrapper.contains(event.target)) closeList();
  });
}
